import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react'
import { IntlService } from "@progress/kendo-react-intl";

const DATE_FORMAT = "M/d/yyyy hh:mm:ss a";
const intl = new IntlService("en");

// Define a service using a base URL and expected endpoints
export const sunbrightApi = createApi({
    reducerPath: 'sunbrightApi',
    baseQuery: fetchBaseQuery({
        baseUrl: 'https://sunbrightvet.com/',
        prepareHeaders: (headers, { getState }) => {
            // By default, if we have a token in the store, let's use that for authenticated requests
            const state = getState();
            const token = (getState() as any).auth.user.token;
            if (token) {
                headers.set('authorization', `Bearer ${token}`)
            }
            return headers
        },
    }),

    tagTypes: ['SDS_Pets', 'SDS_ExamsOpen', 'SDS_ExamsClosed', 'SDS_Exam', 'SDS_Vaccines',
        'SDS_Persons', 'SDS_Person', 'SDS_Agencies', 'SDS_Agency', 'SDS_FollowUps', 'SDS_FollowUp',
        'SDS_Tobaccos', 'SDS_Tobacco', 'SDS_Products', 'SDS_Inventory', 'SDS_ExamsArchive', 'SDS_ExamsExternal'],

    endpoints: (builder) => ({
        getUsers: builder.query<any, string>({
            query: () => `users`,
        }),
        getExams: builder.query<any, string>({
            query: () => `exams`,
            transformResponse: (response: any) => {
                response.forEach((e: any) => {
                    e.updated = e.updated ? new Date(e.updated + " UTC") : null;
                    e.submitted = e.submitted ? new Date(e.submitted + " UTC") : null;
                    e.opened = e.opened ? new Date(e.opened + " UTC") : null;
                    e.closed = e.closed ? new Date(e.closed + " UTC") : null;

                    let appStart = e.appStart ? new Date(e.appStart + " UTC") : null;
                    e.appStart = appStart;
                    e.appEnd = e.appEnd ? new Date(e.appEnd + " UTC") : null;
                    e.appDay = new Date((appStart == null) ? "" : e.appStart.toDateString());
                })
                return response;
            },
            providesTags: ['SDS_ExamsClosed']
        }),
        getExamsActive: builder.query<any, string>({
            query: () => `exams?mode=1`,
            transformResponse: (response: any) => {
                response.forEach((e: any) => {
                    e.updated = e.updated ? new Date(e.updated + " UTC") : null;
                    e.submitted = e.submitted ? new Date(e.submitted + " UTC") : null;
                    e.opened = e.opened ? new Date(e.opened + " UTC") : null;
                    e.closed = e.closed ? new Date(e.closed + " UTC") : null;

                    let appStart = e.appStart ? new Date(e.appStart + " UTC") : null;
                    e.appStart = appStart;
                    e.appEnd = e.appEnd ? new Date(e.appEnd + " UTC") : null;
                    e.appDay = new Date((appStart == null) ? "" : e.appStart.toDateString());
               })
                return response;
            },
            providesTags: ['SDS_ExamsOpen']
        }),
        getExamsArchive: builder.query<any, string>({
            query: () => `exams?mode=2`,
            transformResponse: (response: any) => {
                response.forEach((e: any) => {
                    e.updated = e.updated ? new Date(e.updated + " UTC") : null;
                    e.submitted = e.submitted ? new Date(e.submitted + " UTC") : null;
                    e.opened = e.opened ? new Date(e.opened + " UTC") : null;
                    e.closed = e.closed ? new Date(e.closed + " UTC") : null;

                    let appStart = e.appStart ? new Date(e.appStart + " UTC") : null;
                    e.appStart = appStart;
                    e.appEnd = e.appEnd ? new Date(e.appEnd + " UTC") : null;
                    e.appDay = new Date((appStart == null) ? "" : e.appStart.toDateString());
                })
                return response;
            },
            providesTags: ['SDS_ExamsArchive']
        }),
        getExamsExternal: builder.query<any, string>({
            query: () => `exams?mode=3`,
            transformResponse: (response: any) => {
                response.forEach((e: any) => {
                    e.updated = e.updated ? new Date(e.updated + " UTC") : null;
                    e.submitted = e.submitted ? new Date(e.submitted + " UTC") : null;
                    e.opened = e.opened ? new Date(e.opened + " UTC") : null;
                    e.closed = e.closed ? new Date(e.closed + " UTC") : null;

                    let appStart = e.appStart ? new Date(e.appStart + " UTC") : null;
                    e.appStart = appStart;
                    e.appEnd = e.appEnd ? new Date(e.appEnd + " UTC") : null;
                    e.appDay = new Date((appStart == null) ? "" : e.appStart.toDateString());
                })
                return response;
            },
            providesTags: ['SDS_ExamsExternal']
        }),

        getExam: builder.query<any, string>({
            query: (examID) => `exams/` + examID,
            transformResponse: (response: any) => {
                response.created = intl.parseDate(response.created ? response.created : "", DATE_FORMAT);
                response.submitted = intl.parseDate(response.submitted ? response.submitted : "", DATE_FORMAT);
                response.opened = intl.parseDate(response.opened ? response.opened : "", DATE_FORMAT);
                response.closed = intl.parseDate(response.closed ? response.closed : "", DATE_FORMAT);

                return response;
            },
            providesTags: ['SDS_Exam']
        }),

        getPets: builder.query<any, string>({
            query: () => `pets`,
            transformResponse: (response: any) => {
                response.forEach((e: any) => {
                    e.lastExam = intl.parseDate(e.lastExam ? e.lastExam : "", DATE_FORMAT);
                })
                return response;
            },
            providesTags: ['SDS_Pets']
        }),
        getPet: builder.query<any, string>({
            query: (petID) => `pets/` + petID,
            transformResponse: (response: any) => {
                response.exams.forEach((e: any) => {
                    e.submitted = intl.parseDate(e.submitted ? e.submitted : "", DATE_FORMAT);
                    e.opened = intl.parseDate(e.opened ? e.opened : "", DATE_FORMAT);
                })
                return response;
            }
        }),
        getPetNames: builder.query<any, number>({
            query: (clientID) => `pets?client=` + clientID,
            providesTags: ['SDS_Pets']
        }),

        getPersons: builder.query<any, string>({
            query: () => `persons`,
            providesTags: ['SDS_Persons']
        }),
        getPerson: builder.query<any, string>({
            query: (personID) => `persons/` + personID,
            providesTags: ['SDS_Person']
        }),
        getCwNames: builder.query<any, number>({
            query: (agencyID) => `persons?id=` + (agencyID * -1),
            providesTags: ['SDS_Persons']
        }),
        getClientNames: builder.query<any, string>({
            query: (cwID) => `persons?id=` + cwID,
            providesTags: ['SDS_Persons']
        }),
        getVetNames: builder.query<any, number>({
            query: (cwID) => `persons?id=1`,
            providesTags: ['SDS_Persons']
       }),

        getAgencies: builder.query<any, string>({
            query: () => `agencies`,
            providesTags: ['SDS_Agencies']
        }),
        getAgency: builder.query<any, string>({
            query: (agencyID) => `agencies/` + agencyID,
            providesTags: ['SDS_Agency']
        }),
        getAgencyNames: builder.query<any, string>({
            query: (personID) => `agencies?personID=` + personID,
            providesTags: ['SDS_Agencies']
       }),

        getInternalServices: builder.query<any, string>({
            query: () => `services?type=0`,
        }),
        getExternalServices: builder.query<any, string>({
            query: () => `services?type=1`,
        }),

        getFindings: builder.query<any, string>({
            query: () => `findings`,
        }),

        getVaccines: builder.query<any, number>({
            query: (mode) => `vaccines?mode=` + mode,
            providesTags: ['SDS_Vaccines']
        }),

        getFollowUps: builder.query<any, string>({
            query: () => `events`,
            transformResponse: (response: any) => {
                response.forEach((e: any) => {
                    e.startTime = e.startTime ? new Date(e.startTime + " UTC") : null;
                })
                return response;
            },
           providesTags: ['SDS_FollowUps']
        }),

        getFollowUp: builder.query<any, number>({
            query: (eventID) => `events/` + eventID,
            transformResponse: (response: any) => {
                response.startTime = intl.parseDate(response.startTime ? response.startTime : "", DATE_FORMAT);

                return response;
            },
            providesTags: ['SDS_FollowUp']
        }),

        getFollowUpExams: builder.query<any, number>({
            query: (eventID) => `events/` + (eventID * (-1)),
            transformResponse: (response: any) => {
                return response;
            },
            providesTags: ['SDS_FollowUp']
        }),

        getTobaccos: builder.query<any, string>({
            query: () => `tobaccos?mode=0`,
            transformResponse: (response: any) => {
                response.forEach((e: any) => {
                    e.startTime = e.startTime ? new Date(e.startTime + " UTC") : null;
                })
                return response;
            },
            providesTags: ['SDS_Tobaccos']
        }),

        getTobacco: builder.query<any, string>({
            query: (examID) => `tobaccos/` + examID,
            transformResponse: (response: any) => {
                return response;
            },
            providesTags: ['SDS_Tobacco']
        }),

        getProducts: builder.query<any, string>({
            query: (category) => `products?cat=` + category,
            transformResponse: (response: any) => {
                response.forEach((e: any) => {
                    e.priceupdatedtime = e.priceupdatedtime ? new Date(e.priceupdatedtime + " UTC") : null;
                })
                return response;
            },
            providesTags: ['SDS_Products']
        }),

        getInventory: builder.query<any, string>({
            query: () => `inventory/`,
            transformResponse: (response: any) => {
                response.forEach((e: any) => {
                    e.expirytime = e.expirytime ? new Date(e.expirytime + " UTC") : null;
                })
                return response;
            },
            providesTags: ['SDS_Inventory']
        }),

        getReports: builder.query<any, string>({
            query: (type) => `reports/` + type,
            transformResponse: (response: any) => {
                response.forEach((e: any) => {
                    e.startTime = e.startTime ? new Date(e.startTime + " UTC") : null;
                    e.expirytime = e.expirytime ? new Date(e.expirytime + " UTC") : null;
                    e.updated = e.updated ? new Date(e.updated + " UTC") : null;
                    e.opened = e.opened ? new Date(e.opened + " UTC") : null;
                    e.firstVisit = e.firstVisit ? new Date(e.firstVisit + " UTC") : null;
                    e.lastVisit = e.lastVisit ? new Date(e.lastVisit + " UTC") : null;

                })
                return response;
            },
            providesTags: ['SDS_FollowUps', 'SDS_Inventory']
        }),

       newExam: builder.mutation<any, string>({
            query: (exam) => ({
                url: 'exams',
                method: 'POST',
                body: exam,
           }),
           invalidatesTags: ['SDS_Pets', 'SDS_ExamsOpen', 'SDS_ExamsExternal']
        }),

        editExam: builder.mutation<any, string>({
            query: (exam) => ({
                url: 'exams',
                method: 'POST',
                body: exam,
            }),
            invalidatesTags: ['SDS_Exam', 'SDS_ExamsOpen', 'SDS_ExamsExternal', 'SDS_Pets']
        }),

        editExamStatus: builder.mutation<any, string>({
            query: (exam) => ({
                url: 'exams',
                method: 'POST',
                body: exam,
            }),
            invalidatesTags: ['SDS_Exam', 'SDS_ExamsOpen', 'SDS_ExamsClosed', 'SDS_ExamsExternal', 'SDS_Pets']
        }),

        newVaccine: builder.mutation<any, string>({
            query: (vaccine) => ({
                url: 'vaccines',
                method: 'POST',
                body: vaccine,
            }),
            invalidatesTags: ['SDS_Vaccines']
        }),

        deleteVaccine: builder.mutation<any, string>({
            query: (vaccine) => ({
                url: 'vaccines/del',
                method: 'POST',
                body: vaccine,
            }),
            invalidatesTags: ['SDS_Vaccines']
        }),

        uploadFile: builder.mutation<any, string>({
            query: (formdata) => ({
                url: 'upload',
                method: 'POST',
                body: formdata,
                formData:true,
            }),
            invalidatesTags: ['SDS_Exam']
        }),

        newPerson: builder.mutation<any, string>({
            query: (person) => ({
                url: 'persons',
                method: 'POST',
                body: person,
            }),
            invalidatesTags: ['SDS_Persons', 'SDS_Person']
        }),

        newAgency: builder.mutation<any, string>({
            query: (agency) => ({
                url: 'agencies',
                method: 'POST',
                body: agency,
            }),
            invalidatesTags: ['SDS_Agencies', 'SDS_Agency']
        }),

        newFollowUp: builder.mutation<any, string>({
            query: (followup) => ({
                url: 'events',
                method: 'POST',
                body: followup,
            }),
            invalidatesTags: ['SDS_FollowUps', 'SDS_FollowUp']
        }),

        deleteFollowup: builder.mutation<any, string>({
            query: (followup) => ({
                url: 'events/del',
                method: 'POST',
                body: followup,
            }),
            invalidatesTags: ['SDS_FollowUps']
        }),

        sendMail: builder.mutation<any, string>({
            query: (mail) => ({
                url: 'mail',
                method: 'POST',
                body: mail,
            }),
        }),

        editTobacco: builder.mutation<any, string>({
            query: (tobacco) => ({
                url: 'tobaccos',
                method: 'POST',
                body: tobacco,
            }),
            invalidatesTags: ['SDS_Tobacco']
        }),

        deleteTobacco: builder.mutation<any, string>({
            query: (tobacco) => ({
                url: 'tobaccos/del',
                method: 'POST',
                body: tobacco,
            }),
            invalidatesTags: ['SDS_Tobacco']
        }),

        editProduct: builder.mutation<any, string>({
            query: (product) => ({
                url: 'products',
                method: 'POST',
                body: product,
            }),
            invalidatesTags: ['SDS_Products']
        }),

        editInventory: builder.mutation<any, string>({
            query: (inventory) => ({
                url: 'inventory',
                method: 'POST',
                body: inventory,
            }),
            invalidatesTags: ['SDS_Inventory']
        }),

        deleteInventory: builder.mutation<any, string>({
            query: (inventory) => ({
                url: 'inventory/del',
                method: 'POST',
                body: inventory,
            }),
            invalidatesTags: ['SDS_Inventory']
        }),
    }),
})

// Export hooks for usage in functional components, which are
// auto-generated based on the defined endpoints
export const {
    useGetUsersQuery,
    useGetExamsQuery,
    useGetExamsActiveQuery,
    useGetExamsArchiveQuery,
    useGetExamsExternalQuery,
    useGetExamQuery,
    useGetPetsQuery,
    useGetPetQuery,
    useLazyGetPetQuery,
    useGetPetNamesQuery,
    useLazyGetPetNamesQuery,
    useGetPersonsQuery,
    useGetPersonQuery,
    useGetVetNamesQuery,
    useLazyGetPersonQuery,
    useLazyGetCwNamesQuery,
    useLazyGetClientNamesQuery,
    useGetAgenciesQuery,
    useGetAgencyQuery,
    useGetAgencyNamesQuery,
    useGetInternalServicesQuery,
    useGetExternalServicesQuery,
    useGetFindingsQuery,
    useGetVaccinesQuery,
    useGetFollowUpsQuery,
    useGetFollowUpQuery,
    useGetFollowUpExamsQuery,
    useGetTobaccosQuery,
    useGetTobaccoQuery,
    useGetProductsQuery,
    useGetInventoryQuery,
    useGetReportsQuery,
    useLazyGetReportsQuery,

    useNewExamMutation,
    useEditExamMutation,
    useEditExamStatusMutation,
    useNewVaccineMutation,
    useDeleteVaccineMutation,
    useUploadFileMutation,
    useNewPersonMutation,
    useNewAgencyMutation,
    useNewFollowUpMutation,
    useSendMailMutation,
    useEditTobaccoMutation,
    useDeleteTobaccoMutation,
    useEditProductMutation,
    useEditInventoryMutation,
    useDeleteInventoryMutation,
    useDeleteFollowupMutation,

} = sunbrightApi;
